import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import './ImpactAnalysisPage.css';
import searchIcon from '../assets/Icon/search_icon.svg';
import downloadIcon from '../assets/Icon/download_icon.svg';
import Multiselect from 'multiselect-react-dropdown';
import { apiService } from '../services/apiService';
import { hasPermission, PERMISSION_CODES, filterDataByUserAccess } from '../utils/permissionUtils';
import * as XLSX from 'xlsx';
import notificationService from '../CommanService/notificationService';
import { format } from 'date-fns';

const ImpactAnalysisPage = () => {
  const { user } = useSelector(state => state.auth);
  const [finalisedData, setFinalisedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [interchangeNumbers, setInterchangeNumbers] = useState([]);
  const [finalyzers, setFinalyzers] = useState([]);
  const [partTypes, setPartTypes] = useState([]);
  
  // Selected values state
  const [selectedRegions, setSelectedRegions] = useState(['All']);
  const [selectedPartTypes, setSelectedPartTypes] = useState(['All']);
  const [selectedInterchangeNumbers, setSelectedInterchangeNumbers] = useState(['All']);
  const [selectedFinalyzers, setSelectedFinalyzers] = useState([]);
  
  // Applied filters state (for search button functionality)
  const [appliedFilters, setAppliedFilters] = useState({
    partTypes: ['All'],
    regions: ['All'],
    interchangeNumbers: ['All'],
    finalyzers: ['All']
  });
  
  // Permission check
  const canViewImpactAnalysis = hasPermission(user, PERMISSION_CODES.IMPACT_ANALYSIS);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Get user from session storage
  const getUserFromSession = () => {
    try {
      const userData = sessionStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data from session:', error);
      return null;
    }
  };

  
  // const formatNumber = (num) => {
  //   return new Intl.NumberFormat("en-IN").format(num);
  // };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatNumber1 = (num) => {
    if (num === null || num === undefined) return "";
    return new Intl.NumberFormat('en-IN').format(num);
  }

  // Safe date-time formatter to avoid RangeError on invalid dates
  const formatDateTimeSafe = (value) => {
    try {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return format(d, 'yyyy-MM-dd HH:mm:ss');
    } catch {
      return '';
    }
  };

  // Function to handle Excel export
  const handleDownloadExcel = () => {
    // Get the currently filtered data (same data shown in the table)
    debugger;
    const filteredData = getCurrentFilteredData();
    
    if (filteredData.length === 0) {
      notificationService.showWarning('No filtered data to export');
      return;
    }
    
    // Prepare data for Excel
    const excelData = filteredData.map(item => {
      const originalPrice = parseFloat(item.original_price) || 0;
      const adjustedPrice = parseFloat(item.adjusted_price) || 0;
      const percentageDifference = originalPrice > 0 
        ? ((adjustedPrice - originalPrice) / originalPrice * 100).toFixed(2)
        : '0.00';
      
      return {
        'Part Name': item.part_name || '',
        'Region': item.region || '',
        'IC Number': item.interchange_number || '',
        'Mileage': item.mileage || 0,
        'Original Price': originalPrice,
        'Adjusted Price': adjustedPrice,
        '% Difference': percentageDifference + '%',
        //'Analysed By': item.analyse_by_name || '',
        'Finalized By': item.approved_by_name || ''
      };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Impact Analysis Data');

    // Generate filename with current date     -
    const today = new Date();
    const dateString = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    const fileName = `Impact_Analysis_Report_${dateString}.xlsx`;
    
    // Generate Excel file and download
    XLSX.writeFile(workbook, fileName);
  };


  // Fetch finalised data on component mount
  useEffect(() => {
    const fetchFinalisedData = async () => {
      try {
        setLoading(true);
        const response = await apiService.GetFinalisedData();
        if (response && response.data) {
          setFinalisedData(response.data);
          
          // Extract unique values for dropdowns and add "All" option
          const uniqueRegions = ['All', ...new Set(response.data.map(item => item.region).filter(Boolean))];
          const uniqueInterchanges = ['All', ...new Set(response.data.map(item => item.interchange_number).filter(Boolean))];
          const uniqueFinalyzers = ['All', ...new Set(response.data.map(item => item.approved_by_name).filter(Boolean))];
          const uniquePartName=['All', ...new Set(response.data.map(item => item.part_name).filter(Boolean))]
          setRegions(uniqueRegions);
          setInterchangeNumbers(uniqueInterchanges);
          setFinalyzers(uniqueFinalyzers);
          setPartTypes(uniquePartName);
          
         
          

                     // Always start with "All" for finalyzer to avoid conflicts
           setSelectedFinalyzers(['All']);
        }
      } catch (error) {
        console.error('Error fetching finalised data:', error);
        // Set default values on error
        setRegions(['All', 'MAR', 'NAR', 'EUR']);
        setInterchangeNumbers(['All', '10007', '10008', '10009']);
        setFinalyzers(['All', 'Unknown', 'Admin', 'Manager']);
        setPartTypes(['All', 'ENGINE', 'TRANSMISSION']);
        
                 // Always start with "All" for finalyzer to avoid conflicts
         setSelectedFinalyzers(['All']);
      } finally {
        setLoading(false);
      }
    };

    fetchFinalisedData();
  }, []);

  // Update statistics when applied filters change
  useEffect(() => {
    // This will trigger a re-render with updated statistics
    // when applied filters change (after search button click)
  }, [appliedFilters]);

  // Function to update Region and IC Number dropdowns based on selected Part Types
  const updateDropdownsBasedOnPartTypes = (selectedPartTypes) => {
    if (!finalisedData.length) return;

    console.log('Updating dropdowns based on Part Types:', selectedPartTypes);

    if (selectedPartTypes.includes('All')) {
      // If "All" is selected, show all regions and IC numbers
      const uniqueRegions = ['All', ...new Set(finalisedData.map(item => item.region).filter(Boolean))];
      const uniqueInterchanges = ['All', ...new Set(finalisedData.map(item => item.interchange_number).filter(Boolean))];
      setRegions(uniqueRegions);
      setInterchangeNumbers(uniqueInterchanges);
      console.log('Updated dropdowns for "All" - Regions:', uniqueRegions, 'IC Numbers:', uniqueInterchanges);
    } else {
      // Filter data by ALL selected part types (not just the first one)
      const filteredByPartTypes = finalisedData.filter(item => 
        selectedPartTypes.some(partType => item.part_name === partType)
      );
      console.log(`Filtered data for Part Types [${selectedPartTypes.join(', ')}]:`, filteredByPartTypes.length, 'items');
      
      // Extract unique regions and IC numbers from filtered data
      const uniqueRegions = ['All', ...new Set(filteredByPartTypes.map(item => item.region).filter(Boolean))];
      const uniqueInterchanges = ['All', ...new Set(filteredByPartTypes.map(item => item.interchange_number).filter(Boolean))];
      
      setRegions(uniqueRegions);
      setInterchangeNumbers(uniqueInterchanges);
      console.log(`Updated dropdowns for Part Types [${selectedPartTypes.join(', ')}] - Regions:`, uniqueRegions, 'IC Numbers:', uniqueInterchanges);
      
      // Log detailed breakdown of regions and IC numbers per part type
      selectedPartTypes.forEach(partType => {
        const partTypeData = finalisedData.filter(item => item.part_name === partType);
        const partTypeRegions = [...new Set(partTypeData.map(item => item.region).filter(Boolean))];
        const partTypeICNumbers = [...new Set(partTypeData.map(item => item.interchange_number).filter(Boolean))];
        console.log(`Part Type "${partType}":`, {
          count: partTypeData.length,
          regions: partTypeRegions,
          icNumbers: partTypeICNumbers
        });
      });
      
      // Don't reset selections automatically - let user choose
    }
  };

  // Function to update IC Number dropdown based on selected Region
  const updateICNumbersBasedOnRegion = (selectedRegions) => {
    if (!finalisedData.length) return;

    console.log('Updating IC Numbers based on Regions:', selectedRegions);

    if (selectedRegions.includes('All')) {
      // If "All" is selected, show IC numbers based on current part types filter
      let filteredData = finalisedData;
      
      if (!selectedPartTypes.includes('All')) {
        filteredData = finalisedData.filter(item => 
          selectedPartTypes.some(partType => item.part_name === partType)
        );
      }
      
      const uniqueInterchanges = ['All', ...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
      setInterchangeNumbers(uniqueInterchanges);
      console.log('Updated IC Numbers for "All" regions:', uniqueInterchanges);
    } else {
      // Filter data by selected regions and part types
      let filteredData = finalisedData.filter(item => 
        selectedRegions.some(region => item.region === region)
      );
      
      if (!selectedPartTypes.includes('All')) {
        filteredData = filteredData.filter(item => 
          selectedPartTypes.some(partType => item.part_name === partType)
        );
      }
      
      const uniqueInterchanges = ['All', ...new Set(filteredData.map(item => item.interchange_number).filter(Boolean))];
      setInterchangeNumbers(uniqueInterchanges);
      console.log(`Updated IC Numbers for Regions [${selectedRegions.join(', ')}]:`, uniqueInterchanges);
      
      // Don't reset IC number selection automatically - let user choose
    }
  };

  // Get filtered data based on current selections
  const getFilteredData = () => {
    if (!finalisedData.length) return [];
    
    let filteredData = finalisedData;
    console.log('Initial data count:', filteredData.length);
    console.log('Selected Part Types:', selectedPartTypes);
    
    // Filter by part type
    if (!selectedPartTypes.includes('All')) {
      const beforePartTypeFilter = filteredData.length;
      filteredData = filteredData.filter(item => 
        selectedPartTypes.some(partType => item.part_name === partType)
      );
      console.log(`After Part Type filter (${selectedPartTypes.join(', ')}): ${beforePartTypeFilter} -> ${filteredData.length}`);
      
      // Log the unique part types found in filtered data
      const foundPartTypes = [...new Set(filteredData.map(item => item.part_name))];
      console.log('Found Part Types in filtered data:', foundPartTypes);
    }
    
    // Filter by region
    if (!selectedRegions.includes('All')) {
      const beforeRegionFilter = filteredData.length;
      filteredData = filteredData.filter(item => 
        selectedRegions.some(region => item.region === region)
      );
      console.log(`After Region filter (${selectedRegions.join(', ')}): ${beforeRegionFilter} -> ${filteredData.length}`);
    }
    
    // Filter by IC number
    if (!selectedInterchangeNumbers.includes('All')) {
      const beforeICFilter = filteredData.length;
      filteredData = filteredData.filter(item => 
        selectedInterchangeNumbers.some(icNumber => item.interchange_number === icNumber)
      );
      console.log(`After IC Number filter (${selectedInterchangeNumbers.join(', ')}): ${beforeICFilter} -> ${filteredData.length}`);
    }
    
    // Filter by finalyzer
    if (!selectedFinalyzers.includes('All')) {
      const beforeFinalyzerFilter = filteredData.length;
      filteredData = filteredData.filter(item => 
        selectedFinalyzers.some(finalyzer => item.approved_by_name === finalyzer)
      );
      console.log(`After Finalyzer filter (${selectedFinalyzers.join(', ')}): ${beforeFinalyzerFilter} -> ${filteredData.length}`);
    }
    
    console.log('Final filtered data count:', filteredData.length);
    return filteredData;
  };

  // Handle search button click
  const handleSearch = () => {
    console.log('Search button clicked. Current filters:', {
      regions: selectedRegions,
      partTypes: selectedPartTypes,
      interchangeNumbers: selectedInterchangeNumbers,
      finalyzers: selectedFinalyzers
    });
    
    // Apply the current selections as filters
    setAppliedFilters({
      partTypes: selectedPartTypes,
      regions: selectedRegions,
      interchangeNumbers: selectedInterchangeNumbers,
      finalyzers: selectedFinalyzers
    });
    
    // Reset to first page when applying new filters
    setCurrentPage(1);
  };

  // Handle dropdown selection with "All" logic
  const handleSelect = (selectedList, selectedItem, setSelectedValues) => {
    console.log('Selected:', selectedList);
    
    // If "All" is selected, clear other selections
    if (selectedItem === 'All') {
      setSelectedValues(['All']);
      
      // Update dropdowns when "All" is selected
      if (setSelectedValues === setSelectedPartTypes) {
        updateDropdownsBasedOnPartTypes(['All']);
      } else if (setSelectedValues === setSelectedRegions) {
        updateICNumbersBasedOnRegion(['All']);
      }
    } else {
      // If a specific item is selected, remove "All" from the list
      const filteredList = selectedList.filter(item => item !== 'All');
      setSelectedValues(filteredList);
      
      // Update dropdowns when specific items are selected
      if (setSelectedValues === setSelectedPartTypes) {
        updateDropdownsBasedOnPartTypes(filteredList);
      } else if (setSelectedValues === setSelectedRegions) {
        updateICNumbersBasedOnRegion(filteredList);
      }
    }
  };

  // Handle dropdown removal with "All" logic
  const handleRemove = (selectedList, removedItem, setSelectedValues, options) => {
    console.log('Removed:', selectedList);
    
    // If "All" is being removed, do nothing (keep "All" selected)
    if (removedItem === 'All') {
      return;
    }
    
    // If no items are left after removal, select "All"
    if (selectedList.length === 0) {
      setSelectedValues(['All']);
      
      // Update dropdowns when "All" is selected
      if (setSelectedValues === setSelectedPartTypes) {
        updateDropdownsBasedOnPartTypes(['All']);
      } else if (setSelectedValues === setSelectedRegions) {
        updateICNumbersBasedOnRegion(['All']);
      }
    } else {
      setSelectedValues(selectedList);
      
      // Update dropdowns based on remaining selections
      if (setSelectedValues === setSelectedPartTypes && selectedList.length > 0) {
        updateDropdownsBasedOnPartTypes(selectedList);
      } else if (setSelectedValues === setSelectedRegions && selectedList.length > 0) {
        updateICNumbersBasedOnRegion(selectedList);
      }
    }
  };

  const onSelect = (selectedList, selectedItem) => {
    console.log('Selected:', selectedList);
  };

  const onRemove = (selectedList, removedItem) => {
    console.log('Removed:', selectedList);
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Render pagination controls (matching All Interchanges style)
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <button
        key="prev"
        className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        ←
      </button>
    );

    // First page
    if (startPage > 1) {
      pages.push(
        <button
          key="1"
          className="pagination-btn"
          onClick={() => handlePageChange(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          className="pagination-btn"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    // Next button
    pages.push(
      <button
        key="next"
        className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        →
      </button>
    );

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <div className="d-flex align-items-center">
            <span>Show:</span>
            <select 
              className="form-select mx-2" 
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          <span className="pagination-info-text">
            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
          </span>
        </div>
        <div className="pagination-controls">
          {pages}
        </div>
      </div>
    );
  };

  // Get filtered data for display - only apply filters when search button is clicked

  const getCurrentFilteredData = () => {
    // Only apply filters if search button has been clicked
    if (!appliedFilters.partTypes.includes('All') || 
        !appliedFilters.regions.includes('All') || 
        !appliedFilters.interchangeNumbers.includes('All') || 
        !appliedFilters.finalyzers.includes('All')) {
      
      let filteredData = finalisedData;
      
      // Filter by part type
      if (!appliedFilters.partTypes.includes('All')) {
        filteredData = filteredData.filter(item => 
          appliedFilters.partTypes.some(partType => item.part_name === partType)
        );
      }
      
      // Filter by region
      if (!appliedFilters.regions.includes('All')) {
        filteredData = filteredData.filter(item => 
          appliedFilters.regions.some(region => item.region === region)
        );
      }
      
      // Filter by IC number
      if (!appliedFilters.interchangeNumbers.includes('All')) {
        filteredData = filteredData.filter(item => 
          appliedFilters.interchangeNumbers.some(icNumber => item.interchange_number === icNumber)
        );
      }
      
      // Filter by finalyzer
      if (!appliedFilters.finalyzers.includes('All')) {
        filteredData = filteredData.filter(item => 
          appliedFilters.finalyzers.some(finalyzer => item.approved_by_name === finalyzer)
        );
      }
      
      // Apply user region access filtering
      const userAccessFiltered = filteredData.filter(item => {
        const userRegionAccess = filterDataByUserAccess([item], user, 'region');
        return userRegionAccess.length > 0;
      });
      
      return userAccessFiltered;
    }
    
    // If no filters applied, show all data with user access filtering
    const userAccessFiltered = finalisedData.filter(item => {
      const userRegionAccess = filterDataByUserAccess([item], user, 'region');
      return userRegionAccess.length > 0;
    });
    
    return userAccessFiltered;
  };

  const filteredData = getCurrentFilteredData();

  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Reset to first page when applied filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  // Update dropdowns when finalisedData changes or when part type/region selections change
  useEffect(() => {
    if (finalisedData.length > 0) {
      // Update dropdowns based on current selections
      updateDropdownsBasedOnPartTypes(selectedPartTypes);
      updateICNumbersBasedOnRegion(selectedRegions);
    }
  }, [finalisedData, selectedPartTypes, selectedRegions]);

  // Calculate statistics based on filtered data
  const calculateStatistics = () => {
    const filteredData = getCurrentFilteredData();
    if (!filteredData.length) {
      return {
        priceIncrease: 0,
        priceDecrease: 0,
        samePrice: 0,
        avgInventoryOld: 0,
        avgInventoryNew: 0,
        sumOldPrice: 0,
        sumNewPrice: 0
      };
    }

    let priceIncrease = 0;
    let priceDecrease = 0;
    let samePrice = 0;
    let sumOldPrice = 0;
    let sumNewPrice = 0;

    filteredData.forEach(item => {
      const oldPrice = parseFloat(item.original_price) || 0;
      const newPrice = parseFloat(item.adjusted_price) || 0;
      
      sumOldPrice += oldPrice;
      sumNewPrice += newPrice;

      if (newPrice > oldPrice) {
        priceIncrease++;
      } else if (newPrice < oldPrice) {
        priceDecrease++;
      } else {
        samePrice++;
      }
    });

    return {
      priceIncrease,
      priceDecrease,
      samePrice,
      avgInventoryOld: Math.round(sumOldPrice / filteredData.length),
      avgInventoryNew: Math.round(sumNewPrice / filteredData.length),
      sumOldPrice: Math.round(sumOldPrice),
      sumNewPrice: Math.round(sumNewPrice)
    };
  };

  const stats = calculateStatistics();

  if (loading) {
    return (
      <div style={{
        position: 'fixed',   
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(255,255,255,0.8)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px'
      }}>
        <div className="bouncing-loader">
          <div></div><div></div><div></div>
        </div>
        <p style={{ 
          color: '#333', 
          fontSize: '18px', 
          fontWeight: '600',
          margin: 0,
          textAlign: 'center',
          textShadow: '0 1px 2px rgba(0,0,0,0.1)'
        }}>
          Data is loading, please wait...
        </p>
        <style>
          {`
          .bouncing-loader {
            display: flex;
            align-items: flex-end;
            justify-content: center;
            height: 60px;
            gap: 10px;
          }
          .bouncing-loader div {
            width: 18px;
            height: 18px;
            background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
            border-radius: 50%;
            animation: bouncing-loader 0.6s infinite alternate;
            box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
          }
          .bouncing-loader div:nth-child(2) {
            animation-delay: 0.2s;
          }
          .bouncing-loader div:nth-child(3) {
            animation-delay: 0.4s;
          }
          @keyframes bouncing-loader {
            to {
              transform: translateY(-30px) scale(1.1);
              box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
            }
          }
          `}
        </style>
      </div>
    );
  }

  return (
    <div className="">
      <div className="filter_section d-flex align-items-start row mb_25">
              <div className="filter_items col-md-3">
          <label className="font_14 fw-med text_black">
            Part Type
            {!selectedPartTypes.includes('All') && selectedPartTypes.length > 0 && (
              <span className="badge bg-primary ms-2" style={{ fontSize: '10px' }}>
                {selectedPartTypes.length} selected
              </span>
            )}
          </label>
          <Multiselect
            isObject={false}
            options={partTypes}
            selectedValues={selectedPartTypes}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedPartTypes);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedPartTypes, partTypes);
            }}
            placeholder="Select Part Type"
          />
        </div> 

        <div className="filter_items col-md-3">
          <label className="font_14 fw-med text_black">
            Regions
            {!selectedRegions.includes('All') && selectedRegions.length > 0 && (
              <span className="badge bg-primary ms-2" style={{ fontSize: '10px' }}>
                {selectedRegions.length} selected
              </span>
            )}
          </label>
                       <Multiselect
             isObject={false}
             options={regions}
             selectedValues={selectedRegions}
             onSelect={(selectedList, selectedItem) => {
               handleSelect(selectedList, selectedItem, setSelectedRegions);
             }}
             onRemove={(selectedList, removedItem) => {
               handleRemove(selectedList, removedItem, setSelectedRegions, regions);
             }}
             placeholder="Select Regions"
           />
        </div>
         
        <div className="filter_items col-md-3">
          <label className="font_14 fw-med text_black">
            IC Number
            {!selectedInterchangeNumbers.includes('All') && selectedInterchangeNumbers.length > 0 && (
              <span className="badge bg-primary ms-2" style={{ fontSize: '10px' }}>
                {selectedInterchangeNumbers.length} selected
              </span>
            )}
          </label>
          <Multiselect
            isObject={false}
            options={interchangeNumbers}
            selectedValues={selectedInterchangeNumbers}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedInterchangeNumbers);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedInterchangeNumbers, interchangeNumbers);
            }}
            placeholder="Select IC Number"
          />
        </div>
        <div className="filter_items col-md-3">
          <label className="font_14 fw-med text_black">Finalyzer</label>
          <Multiselect
            isObject={false}
            options={finalyzers}
            selectedValues={selectedFinalyzers}
            onSelect={(selectedList, selectedItem) => {
              handleSelect(selectedList, selectedItem, setSelectedFinalyzers);
            }}
            onRemove={(selectedList, removedItem) => {
              handleRemove(selectedList, removedItem, setSelectedFinalyzers, finalyzers);
            }}
            placeholder="Finalyzers"
          />
        </div>
                 <div className="filter_button_blk mt_26">
           <img 
             src={searchIcon} 
             alt="search" 
             className="" 
             onClick={handleSearch}
             style={{ 
               cursor: 'pointer',
               transition: 'transform 0.2s'
             }}
             onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
             onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
           />
         </div>
      </div>
       <div className="row report_container mb_10">
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Number of Parts With Price Increase</span>
            <h4 className="font_24 fw-semi text_black">{formatNumber1(stats.priceIncrease)}</h4>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Number of Parts With Price Decrease</span>
            <h4 className="font_24 fw-semi text_black">{formatNumber1(stats.priceDecrease)}</h4> 
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Number of Parts With Same Price</span>
            <h4 className="font_24 fw-semi text_black">{formatNumber1(stats.samePrice)}</h4> 
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Average Inventory @old Price</span>
            <h4 className="font_24 fw-semi text_black">  {`${formatNumber(stats.avgInventoryOld)} $`}</h4>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Average Inventory @New Price</span>
            <h4 className="font_24 fw-semi text_black">{`${formatNumber(stats.avgInventoryNew)} $`}</h4>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Sum of Old Price</span>
            <h4 className="font_24 fw-semi text_black">{`${formatNumber(stats.sumOldPrice)} $`}</h4> 
          </div>
        </div>
        <div className="col-md-3">
          <div className="report_info_blks">
            <span className="font_14 fw-reg text_black mb_10 d-block">Sum of New Price</span>
            <h4 className="font_24 fw-semi text_black">{`${formatNumber(stats.sumNewPrice)} $`}</h4> 
          </div>
        </div>
      </div>
      <div className="p_15 report_table_bg impact_data_blk">
        <div className="d-flex justify-content-between align-items-center mb_10">
          <h4 className="font_18 fw-semi mb_0">Finalized Data</h4>
          <button 
            className="btn btn-outline-primary d-flex align-items-center"
            onClick={handleDownloadExcel}
            style={{ 
              border: '1px solid #ff6600', 
              borderRadius: '4px', 
              padding: '8px 12px',
              cursor: 'pointer',
              backgroundColor: 'transparent',
              color: 'black'
            }}
            title="Export to Excel"
          >
            <img 
              src={downloadIcon} 
              alt="Download" 
              style={{ width: '16px', height: '16px', marginRight: '8px' }}
            />
            Export Report
          </button>
        </div>
        <table className="w-100 report_table impact_analysis_table">
          <thead>
            <tr>
              <th>Part Name</th>
              <th>Region</th>
              <th>IC Number</th>
              <th>Mileage</th>
              <th>Original Price</th>
              <th>Adjusted Price</th>
               <th>price Difference</th>
               <th>% difference</th>
              {/* <th>Analysed By</th> */}
              <th>Date&Time </th>
              <th>Finalized By</th>
            </tr>
          </thead>
                     <tbody>
             {currentData.map((item, index) => {
               // Calculate percentage difference
               const originalPrice = parseFloat(item.original_price) || 0;
               const adjustedPrice = parseFloat(item.adjusted_price) || 0;
               
               // Debug logging to check values
               console.log(`Row ${index}: Original=${originalPrice}, Adjusted=${adjustedPrice}`);
               
               const percentageDifference = originalPrice > 0 
                 ? ((adjustedPrice - originalPrice) / originalPrice).toFixed(2)
                 : '0.00';
               
               // Debug logging for calculation
               if (originalPrice > 0) {
                 const calculation = ((adjustedPrice - originalPrice) / originalPrice * 100);
                 console.log(`Row ${index}: Calculation = (${adjustedPrice} - ${originalPrice}) / ${originalPrice} * 100 = ${calculation}`);
               }
               
               // Determine arrow and color based on percentage difference
               const percentageValue = parseFloat(percentageDifference);
               let arrowIcon = '';
               let arrowColor = '';
               
               if (percentageValue > 0) {
                 arrowIcon = '▲'; // Up arrow for increase
                 arrowColor = '#28a745'; // Green color for arrow
               } else if (percentageValue < 0) {
                 arrowIcon = '▼'; // Down arrow for decrease
                 arrowColor = '#dc3545'; // Red color for arrow
               } else {
                 arrowIcon = '—'; // Minus sign for no change
                 arrowColor = '#6c757d'; // Gray color for arrow
               }
               
               return (
                 <tr key={item.id || index}>
                   <td>{item.part_name}</td>
                   <td>{item.region}</td>
                   <td>{item.interchange_number}</td>
                   <td>{formatNumber(item.mileage)}</td>
                   <td>{`${formatNumber(item.original_price)} $`}</td>
                   <td>{`${formatNumber(item.adjusted_price)} $`}</td>
                   <td>{item.price_Difference}</td>
                   <td>
                     <span style={{ 
                       color: arrowColor,
                       fontSize: '14px',
                       fontWeight: 'bold',
                       marginRight: '8px'
                     }}>
                       {arrowIcon}
                     </span>
                     <span style={{ color: '#000' }}>
                       {percentageDifference}%
                     </span>
                   </td>
                   {/* <td>{item.analyse_by_name || ''}</td> */}
                   <td>{formatDateTimeSafe(item.created_on)}</td>
                   <td>{item.approved_by_name}</td>
                 </tr>
               );
             })}
           </tbody>
                 </table>
         
         {/* Pagination Controls */}
         {renderPagination()}
       </div>
     </div>
   );
 };

export default ImpactAnalysisPage;
